<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
  -->

<template>
  <div class="account-details">
    <v-container>
      <h1>Account Overview</h1>
      <p class="page-description">{{ pageDescription }}</p>

      <v-layout v-if="trial" row wrap class="card-container">
        <v-flex d-flex xs6 class="card-wrapper">
          <v-card>
            <v-card-title>Trial Plan</v-card-title>
            <v-card-text>
              Your trial grants you {{ trialSearchLimit }} searches per month,
              resetting on the first of every month. Check out our other plans
              for batch searching and other premium features.
            </v-card-text>
            <v-card-actions>
              <a @click.stop="openTrialUpgradeDialog">See plan options</a>
            </v-card-actions>
          </v-card>
        </v-flex>

        <v-flex d-flex xs6 class="card-wrapper">
          <v-card>
            <v-card-title>Searches remaining</v-card-title>
            <v-card-text>
              You have searched
              {{ trialSearchLimit - trialSearchesLeft }} time(s) this month. You
              have {{ trialSearchesLeft }} searches remaining.
            </v-card-text>
            <v-card-actions>
              <a @click.stop="openTrialUpgradeDialog">Get unlimited searches</a>
            </v-card-actions>
          </v-card>
        </v-flex>

        <v-flex d-flex class="card-wrapper">
          <help-card class="transparent-card" />
        </v-flex>
      </v-layout>

      <v-layout v-else-if="paid" row wrap class="card-container">
        <v-flex d-flex xs6>
          <v-layout row wrap>
            <v-flex d-flex class="card-wrapper">
              <v-card>
                <v-card-title>{{ planName }}</v-card-title>
                <v-card-text v-if="!disabling">
                  Your plan will automatically renew on
                  {{ formatNextBillingDate }} for ${{ basePrice }} + your
                  monthly search fees.
                </v-card-text>
                <v-card-text v-else>
                  Your plan will end on {{ formatNextBillingDate }}.
                </v-card-text>

                <v-card-actions>
                  <v-dialog
                    v-if="!disabling"
                    v-model="teamOptionsDialog"
                    max-width="500px"
                    content-class="app-dialog"
                  >
                    <a slot="activator">Change your plan</a>
                    <h2>Change your team plan</h2>
                    <team-options v-model="teamType" />

                    <p class="note">
                      (To downgrade your team plan please contact support.)
                    </p>
                    <div class="actions">
                      <a href="" @click.prevent="teamOptionsDialog = false"
                        >Cancel</a
                      >
                      <a
                        href=""
                        @click.prevent="
                          teamOptionsDialog = false
                          saveTeamOptions()
                        "
                        >Save Changes</a
                      >
                    </div>
                  </v-dialog>

                  <v-dialog
                    v-else
                    v-model="reactivateDialog"
                    max-width="500px"
                    content-class="app-dialog"
                  >
                    <a slot="activator">Reactivate your account</a>
                    <h2>Welcome Back!</h2>

                    <p>We're happy to hear you want to keep your account.</p>

                    <div class="actions">
                      <a href="" @click.prevent="reactivateDialog = false"
                        >Cancel</a
                      >
                      <a
                        href=""
                        @click.prevent="
                          reactivateDialog = false
                          reactivateAccount()
                        "
                        >Reactivate Account</a
                      >
                    </div>
                  </v-dialog>
                </v-card-actions>
              </v-card>
            </v-flex>

            <v-flex d-flex class="card-wrapper">
              <v-card>
                <v-card-title>Billing & Payment</v-card-title>
                <v-card-text>
                  You are the Account Owner for your organization’s account. To
                  change this role to someone else, contact us.
                </v-card-text>

                <braintree-dropin
                  :key="braintreeKey"
                  ref="braintreeDropIn"
                  :customer-id="braintreeCustomerId"
                  @ready="braintreeInstance = $event"
                />

                <v-card-actions>
                  <v-btn
                    color="primary"
                    :loading="paymentLoading"
                    @click="updateCCPayment"
                    :disabled="paymentLoading"
                  >
                    Update payment method
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex d-flex xs6>
          <v-layout row wrap>
            <v-flex d-flex class="card-wrapper">
              <v-card>
                <v-card-title>Search Types</v-card-title>
                <v-card-text>
                  View your company’s search usage this billing cycle and add or
                  remove search types available to your teams.
                </v-card-text>

                <v-data-table
                  :headers="lineItemsTableHeaders"
                  :items="lineItems"
                  hide-actions
                >
                  <template slot="items" slot-scope="props">
                    <td class="text-xs-left cell">
                      {{ props.item.description }}
                    </td>
                    <td class="text-xs-left cell">
                      ${{ props.item.unitPrice }}
                    </td>
                    <td class="text-xs-right cell">
                      {{ props.item.quantity }}
                    </td>
                    <td class="text-xs-right cell">${{ props.item.total }}</td>
                  </template>
                </v-data-table>

                <v-card-actions>
                  <v-dialog
                    v-model="searchOptionsDialog"
                    max-width="500px"
                    content-class="app-dialog"
                  >
                    <a slot="activator">Add search types</a>
                    <search-options
                      title="Add search types"
                      v-model="searchOptions"
                      :disabled-options="disabledSearchOptions"
                    />

                    <p class="note">
                      (To remove search types please contact support.)
                    </p>
                    <div class="actions">
                      <a href="" @click.prevent="searchOptionsDialog = false"
                        >Cancel</a
                      >
                      <a
                        href=""
                        @click.prevent="
                          searchOptionsDialog = false
                          saveSearchOptions()
                        "
                        >Save Changes</a
                      >
                    </div>
                  </v-dialog>
                </v-card-actions>
              </v-card>
            </v-flex>

            <v-flex d-flex class="card-wrapper">
              <!--
                technically, trial clients are also cancellable(whose disabling flag can be set to true), however, the cancellation feature
                is only concerned with paid users.
              -->
              <help-card
                @cancel-account="cancelAccount"
                :cancellable="!disabling && !trial"
                :next-billing-date="formatNextBillingDate"
                class="transparent-card"
              />
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>

    <v-snackbar
      top
      :timeout="10000"
      :color="toasterMessage.mode"
      :value="toasterMessage.text"
    >
      <span v-html="toasterMessage.text" />

      <a @click.stop="clearMessage"> Close </a>
    </v-snackbar>

    <v-dialog
      v-if="trial"
      v-model="trialUpgradeDialog"
      max-width="500px"
      persistent
      content-class="app-dialog"
    >
      <section v-if="trialUpgradeStep === 1">
        <h2>Choose a plan</h2>
        <team-options v-model="teamType" />
      </section>
      <section v-else-if="trialUpgradeStep === 2">
        <search-options
          title="What types of searches do you need?"
          v-model="searchOptions"
          :disabled-options="disabledSearchOptions"
        />
      </section>
      <section v-else-if="trialUpgradeStep === 3">
        <h2 class="mb-0">Payment Info</h2>
        <braintree-dropin
          :key="braintreeKey"
          ref="braintreeDropIn"
          @ready="braintreeInstance = $event"
        />
        <p class="mb-0">
          You will have a chance to review the options you have selected and the
          information you are submitting on the next screen.
        </p>
      </section>
      <section v-else-if="trialUpgradeStep === 4">
        <h2>Confirm</h2>
        <h2 class="subtitle">ExclusionCheck Company Plan Details</h2>
        <line-item-card title="Team Selection" :items="basePricingItem" />
        <line-item-card
          title="Search Options"
          :items="searchOptionPricingItems"
        />
        <line-item-card
          title="Payment Info"
          :items="[paymentInfo]"
          borderless
        />
      </section>

      <div class="actions spaced">
        <a href="" @click.prevent="closeTrialUpgradeDialog">Cancel</a>
        <v-btn color="primary" @click="upgradeActions"
          >{{ trialUpgradeDialogActionBtn }}
        </v-btn>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios"
import moment from "moment"
import SearchOptions from "@/components/SearchOptions"
import TeamOptions from "@/components/TeamOptions"
import HelpCard from "@/components/AccountDetailsHelpCard"
import BraintreeDropin from "@/components/BraintreeDropin"
import LineItemCard from "@/components/LineItemCard"
import { getPricingItems } from "@/service/lineitem"
import analytics from "@/service/analytics"
import { mapActions, mapGetters } from "vuex"

import toasterMixin from "@/mixins/toaster"

const endpoint = "/services/account"

/**
 * This is a bit of a hack to get around of the fact that dropin UI can't do deletes/updates
 */
function updateCustomerCreditCards(customerId, nonce) {
  return axios.put(`/services/braintree/customer/${customerId}`, { nonce })
}

export default {
  name: "AccountDetails",
  mixins: [toasterMixin],

  data: () => ({
    disabling: false,
    reactivateDialog: false,

    braintreeCustomerId: null,
    braintreeInstance: null,
    braintreeKey: 0,
    updatedNonce: null,
    lastFour: null,
    paymentLoading: null,

    isTrial: null,
    trialSearchesLeft: null,
    trialSearchLimit: 10,
    trialUpgradeDialog: false,
    trialUpgradeStep: 1,

    teamOptionsDialog: false,
    teamType: null,
    initialTeamType: null,
    searchOptionsDialog: false,
    searchOptions: [],

    basePrice: null,
    nextBillingDate: null,
    lineItemsTableHeaders: [
      {
        text: "Search Type",
        value: "description",
        align: "left",
        class: "header"
      },
      { text: "Price", value: "unitPrice", align: "left", class: "header" },
      { text: "Searches", value: "quantity", align: "right", class: "header" },
      { text: "Subtotal", value: "total", align: "right", class: "header" }
    ],
    lineItems: []
  }),
  computed: {
    ...mapGetters(["username"]),
    trial() {
      return this.isTrial === true
    },
    paid() {
      return this.isTrial === false
    },
    formatNextBillingDate() {
      return moment.utc(this.nextBillingDate).format("L")
    },
    planName() {
      if (this.trial) {
        return "Trial Plan"
      }
      return this.initialTeamType === "SINGLE"
        ? "Single Team Plan"
        : "Unlimited Teams Plan"
    },
    pageDescription() {
      // TODO
      return this.trial
        ? "View remaining searches this month and upgrade to a paid plan."
        : "Change your plan, add or remove search options, and update your payment method."
    },
    disabledSearchOptions() {
      // TODO
      return this.trial ? ["NAME"] : this.initialSearchOptions
    },
    trialUpgradeDialogActionBtn() {
      return this.trialUpgradeStep === 4 ? "Looks Good!" : "Next"
    },
    basePricingItem() {
      return getPricingItems([this.teamType])
    },
    searchOptionPricingItems() {
      return getPricingItems(this.searchOptions)
    },
    paymentInfo() {
      return {
        name: `Card ending in *${this.lastFour}`
      }
    }
  },
  watch: {
    teamType(newVal, oldVal) {
      if (
        newVal === "SINGLE" &&
        oldVal === "MULTI_TEAM" &&
        this.initialTeamType === "MULTI_TEAM"
      ) {
        this.showMessage(
          "To downgrade your team plan please contact support.",
          "info"
        )
        this.teamType = oldVal
      }
    }
  },
  created() {
    this.getAccountDetails()
  },
  methods: {
    ...mapActions(["getECUserInfo"]),

    getAccountDetails() {
      return axios.get(endpoint).then(response => {
        const accountDetails = response.data
        this.disabling = accountDetails.disabling
        this.basePrice = accountDetails.basePricingItem.price
        this.nextBillingDate = accountDetails.nextBillingDate
        this.lineItems = accountDetails.lineItems
        this.searchOptions = accountDetails.searchOptions
        this.initialSearchOptions = accountDetails.searchOptions
        this.initialTeamType = this.teamType = accountDetails.teamType
        this.braintreeCustomerId = accountDetails.braintreeCustomerId
        this.isTrial = accountDetails.trial
        this.trialSearchesLeft = accountDetails.trialSearchesLeft
      })
    },

    saveSearchOptions() {
      axios
        .put(`${endpoint}/searchoptions`, this.searchOptions)
        .then(
          () =>
            this.showMessage(
              "Search Types updated successfully. Please logout and login again."
            ),
          error =>
            this.showMessage(
              `Search Types uodate failed. Error: ${error.response.data.text}`,
              "error"
            )
        )
        .then(this.getAccountDetails)
        .then(this.getECUserInfo)
    },

    saveTeamOptions() {
      axios
        .put(`${endpoint}/teamoptions`, this.teamType)
        .then(
          () => this.showMessage("Team Plan updated successfully."),
          error =>
            this.showMessage(
              `Team Plan uodate failed. Error: ${error.response.data.text}`,
              "error"
            )
        )
        .then(this.getAccountDetails)
    },

    reInitBrainTreeDropIn() {
      this.braintreeKey = new Date().getTime()

      return new Promise(resolve => {
        const unwatch = this.$watch("braintreeInstance", newValue => {
          unwatch()
          resolve(newValue)
        })
      })
    },

    requestBraintreePayment() {
      return this.braintreeInstance
        .requestPaymentMethod()
        .then(({ nonce, details }) => {
          this.updatedNonce = nonce
          this.lastFour = details.lastFour
        })
    },
    /**
     * Note, this is not really an update without a hack done in the server side, since existing one can't be deleted for now.
     * See: https://github.com/braintree/braintree-ios-drop-in/issues/57
     */
    updateCCPayment() {
      this.paymentLoading = true
      this.requestBraintreePayment()
        .then(() =>
          updateCustomerCreditCards(this.braintreeCustomerId, this.updatedNonce)
        )
        .then(() => this.reInitBrainTreeDropIn())
        .then(() => this.showMessage("Credit Card updated successfully."))
        .catch(err => console.error(err))
        .finally(() => (this.paymentLoading = false))
    },
    closeTrialUpgradeDialog() {
      this.trialUpgradeDialog = false
    },
    openTrialUpgradeDialog() {
      this.trialUpgradeStep = 1
      this.trialUpgradeDialog = true
    },
    upgradeActions() {
      if (this.trialUpgradeStep === 3) {
        this.requestBraintreePayment()
          .then(() => this.trialUpgradeStep++)
          .catch(err => console.error(err))
      } else if (this.trialUpgradeStep === 4) {
        this.trialUpgrade().then(() => this.closeTrialUpgradeDialog())
      } else {
        this.trialUpgradeStep++
      }
    },
    trialUpgrade() {
      return axios
        .put(endpoint, {
          nonce: this.updatedNonce,
          searchOptions: this.searchOptions,
          teamType: this.teamType
        })
        .then(
          () =>
            this.showMessage(
              "Thanks for upgrading to an ExclusionCheck company account! Please logout and login again, then view and manage your new account details below."
            ),
          error =>
            this.showMessage(
              `company account upgrade failed. Error: ${error.response.data.text}`,
              "error"
            )
        )
        .then(this.getAccountDetails)
        .then(this.getECUserInfo)
    },
    cancelAccount() {
      axios
        .put(`${endpoint}/cancel`)
        .then(() =>
          this.showMessage(`We're sorry to see you go!.<br>
Your account has been canceled, you'll have access until ${this.formatNextBillingDate}.`)
        )
        .then(() => analytics.setUserProperties(this.username))
        .then(() => analytics.logAnalytic("Cancels Account"))
        .then(this.getAccountDetails)
    },
    reactivateAccount() {
      axios
        .put(`${endpoint}/reactivate`)
        .then(() => analytics.setUserProperties(this.username))
        .then(() => analytics.logAnalytic("Reactivates Account"))
        .then(this.getAccountDetails)
    }
  },
  components: {
    LineItemCard,
    BraintreeDropin,
    SearchOptions,
    TeamOptions,
    HelpCard
  }
}
</script>

<style lang="scss" scoped>
.account-details {
  color: $color-ec-black1;

  > .container {
    padding-top: 3.375rem;
  }
  .page-description {
    margin-bottom: 3rem;
  }
  h1 {
    font-weight: $heavy;
  }
  .v-card {
    padding: 1.5rem 1.875rem;
  }
  .transparent-card {
    background-color: transparent;
    box-shadow: none;
    padding: 0;
  }
  .v-card__actions {
    justify-content: flex-end;
  }
  .card-container {
    margin: -1.5rem;
  }
  .card-wrapper {
    padding: 1.5rem;
    .v-card {
      width: 100%;
    }
  }
  .cell {
    font-size: 1rem;
    padding: 0 0.75rem;
  }
  /deep/ th.header {
    @extend .cell;
    font-weight: $heavy;
    white-space: normal;
  }
}

.app-dialog {
  .note {
    font-style: italic;
    font-size: 0.875rem;
    margin: 1rem auto;
  }
}
</style>

<style lang="scss">
.app-dialog {
  .app-search-type-list {
    .app-search-type {
      .app-price,
      label {
        font-size: 1rem;
      }
      .v-icon {
        font-size: 1.25rem;
      }
      .v-messages {
        font-size: 0.75rem !important;
      }
    }
  }
}

.account-details {
  .v-card__title {
    font-size: 1.5rem;
    font-weight: $heavy;
    padding: 0;
    margin-bottom: 0.75rem;
  }
  .v-card__text {
    padding: 0;
    margin-bottom: 1rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AccountDetails.vue